/**
 * A folder of real documents, after the "Imported" reference: a dark slate back pocket whose tab sits top-right (two
 * small bars on it); the edges of the paper peeking along the top (one sheet per document, up to three; a missing
 * document is a dashed edge); and a matte slate front, its tab raised on the left, a lit top edge and a soft drop.
 * The front carries an icon, the folder's name and its count. 11:8 at any width.
 * On hover the paper rises a little out of the pocket and the front tips back.
 * Used only where the thing really is a folder of documents.
 */
export interface DocFolderSheet {
  title: string;
  meta?: string;
  /** an expected document that isn't on file: drawn as a dashed edge */
  missing?: boolean;
}

// the front in a 100 × 80 box: a tab raised on the left, easing down to the body
const FRONT_PATH =
  'M0 7 Q0 0 7 0 H31 Q35.5 0 38.5 3.4 L43.4 9 Q46 12 50 12 H93 Q100 12 100 19 V73 Q100 80 93 80 H7 Q0 80 0 73 Z';
const FRONT_MASK = `url("data:image/svg+xml;utf8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 80' preserveAspectRatio='none'>" +
    `<path d='${FRONT_PATH}' fill='black'/></svg>`,
)}")`;

const CSS = `
.hx-folder-obj { position: relative; perspective: 1100px; }
.hx-folder:hover { background: var(--tx-fill); }
.hx-folder[aria-pressed='true']:hover { background: var(--tx-sel-bg); }
.hx-folder-sheet, .hx-folder-front { transition: transform 0.55s cubic-bezier(0.32, 0.72, 0, 1); will-change: transform; }
.hx-folder-front { transform-origin: 50% 100%; }
.hx-folder:hover .hx-folder-front, .hx-folder:focus-visible .hx-folder-front { transform: rotateX(-8deg); }
.hx-folder-sheet { transform-origin: 50% 100%; }
/* the sheet's name is drawn from data-title, so it isn't duplicated in the page's text (the folder carries it) */
.hx-folder-sheet-title::before { content: attr(data-title); }
.hx-folder-sheet-title { opacity: 0; transition: opacity 0.25s ease 0.08s; }
.hx-folder:hover .hx-folder-sheet-title, .hx-folder:focus-visible .hx-folder-sheet-title { opacity: 1; }
/* the fan: each sheet pivots from its base, the outer ones tipping away, all rising out of the pocket */
.hx-folder:hover .hx-folder-sheet[data-fan='l'], .hx-folder:focus-visible .hx-folder-sheet[data-fan='l'] {
  transform: translate(-30%, -20%) rotate(-15deg);
}
.hx-folder:hover .hx-folder-sheet[data-fan='c'], .hx-folder:focus-visible .hx-folder-sheet[data-fan='c'] {
  transform: translateY(-30%);
}
.hx-folder:hover .hx-folder-sheet[data-fan='r'], .hx-folder:focus-visible .hx-folder-sheet[data-fan='r'] {
  transform: translate(30%, -20%) rotate(15deg);
}
@media (prefers-reduced-motion: reduce) {
  .hx-folder:hover .hx-folder-sheet, .hx-folder:hover .hx-folder-front { transform: none !important; }
}
`;

export const DocFolder = ({
  sheets,
  label,
  sublabel,
  count,
  pill,
  badges = [],
  onClick,
  active = false,
}: {
  sheets: DocFolderSheet[];
  /** the folder's name, on the front */
  label?: string;
  sublabel?: string;
  /** e.g. "5 files", under the name */
  count?: string;
  /** e.g. the newest file's date, after the count */
  pill?: string;
  /** small round marks on the front, bottom-left (e.g. the file types inside: "PDF") */
  badges?: string[];
  onClick?: () => void;
  /** the open folder: its front lifts a step */
  active?: boolean;
}) => {
  const shown = sheets.slice(0, 3);
  const caption = [count, pill, sublabel].filter(Boolean).join(' · ');

  return (
    <div
      className="hx-folder"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? active : undefined}
      aria-label={label}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        // the folder sits in its own tile (the reference's grid): quiet until hovered, the raised tile once open
        width: '100%',
        boxSizing: 'border-box',
        padding: '20px 16px 22px',
        borderRadius: 18,
        background: active ? 'var(--tx-sel-bg)' : undefined,
        boxShadow: active ? 'var(--tx-sel-shadow)' : undefined,
        fontFamily: "'Manrope', sans-serif",
        cursor: onClick ? 'pointer' : undefined,
        outline: 'none',
        transition: 'background 0.15s ease',
      }}>
      <style>{CSS}</style>
      <div className="hx-folder-obj" style={{ aspectRatio: '11 / 8', width: '88%', margin: '0 auto' }}>
        {/* the back pocket, its tab top-right with two small bars */}
        <svg
          aria-hidden
          viewBox="0 0 110 80"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          <path
            d="M4 14 Q4 8 10 8 H62 Q65 8 67 5.6 L68.6 3.4 Q70.6 0 74.4 0 H96 Q102 0 102 6 V72 Q102 78 96 78 H10 Q4 78 4 72 Z"
            fill="#262626"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <rect x="78" y="3" width="9" height="1.6" rx="0.8" fill="rgba(255,255,255,0.1)" />
          <rect x="89.5" y="3" width="5" height="1.6" rx="0.8" fill="rgba(255,255,255,0.1)" />
        </svg>

        {/* the paper's edges along the top: one per document, stepped */}
        {shown.map((sheet, i) => {
          const slot = 3 - shown.length + i;
          // which way this sheet fans: one rises, two split, three spread left / centre / right
          const fan = shown.length === 1 ? 'c' : shown.length === 2 ? (i === 0 ? 'l' : 'r') : (['l', 'c', 'r'] as const)[i];
          return (
            <div
              key={`${sheet.title}-${i}`}
              className="hx-folder-sheet"
              data-slot={slot}
              data-fan={fan}
              title={sheet.meta ? `${sheet.title} · ${sheet.meta}` : sheet.title}
              style={{
                position: 'absolute',
                top: `${10 + slot * 2.6}%`,
                left: `${20 + slot * 1.5}%`,
                right: `${20 - slot * 1.5}%`,
                bottom: '20%',
                borderRadius: 8,
                padding: '7% 8% 0',
                boxSizing: 'border-box',
                overflow: 'hidden',
                background: sheet.missing
                  ? 'rgba(38,38,37,0.6)'
                  : slot === 2
                    ? 'linear-gradient(180deg, #f6f5f1 0%, #e2e0da 100%)'
                    : slot === 1
                      ? 'linear-gradient(180deg, #e6e4df 0%, #cfcdc7 100%)'
                      : 'linear-gradient(180deg, #d2d0ca 0%, #bab8b2 100%)',
                border: sheet.missing ? '1.5px dashed rgba(247,247,246,0.36)' : 'none',
                boxShadow: sheet.missing ? 'none' : '0 6px 18px -8px rgba(0,0,0,0.55)',
                zIndex: 1 + slot,
              }}>
              <div
                className="hx-folder-sheet-title"
                aria-hidden
                data-title={sheet.title}
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  lineHeight: 1.25,
                  color: sheet.missing ? 'rgba(247,247,246,0.7)' : '#2b2a28',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              />
              {!sheet.missing && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                  {[88, 74, 82, 60].map((w, k) => (
                    <span key={k} style={{ height: 2.5, width: `${w}%`, borderRadius: 2, background: 'rgba(38,38,37,0.12)' }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* the matte slate front */}
        <div
          className="hx-folder-front"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '84%',
            zIndex: 10,
            WebkitMaskImage: FRONT_MASK,
            maskImage: FRONT_MASK,
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            background: active
              ? 'linear-gradient(180deg, rgba(70,70,69,0.94) 0%, rgba(52,52,51,0.97) 100%)'
              : 'linear-gradient(180deg, rgba(60,60,59,0.93) 0%, rgba(44,44,43,0.97) 100%)',
          }}>
          <svg
            aria-hidden
            viewBox="0 0 100 80"
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
            <defs>
              <radialGradient id="hx-folder-glow" cx="0.45" cy="0.3" r="0.6">
                <stop offset="0" stopColor="rgba(255,255,255,0.05)" />
                <stop offset="1" stopColor="rgba(255,255,255,0)" />
              </radialGradient>
            </defs>
            <path d={FRONT_PATH} fill="url(#hx-folder-glow)" />
            <path
              d="M0.6 7 Q0.6 0.6 7 0.6 H31 Q35.2 0.6 38.1 3.8 L43 9.4 Q45.7 12.6 50 12.6 H93 Q99.4 12.6 99.4 19"
              fill="none"
              stroke="rgba(255,255,255,0.14)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {(label || caption) && (
            <div style={{ position: 'absolute', left: '7%', right: badges.length ? '24%' : '7%', bottom: '11%', minWidth: 0 }}>
              {label && (
                <div
                  style={{
                    // the full name, wrapping to a second line rather than cutting off
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.25,
                    color: 'var(--tx-text)',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textWrap: 'balance',
                  }}>
                  {label}
                </div>
              )}
              {caption && (
                <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 2, whiteSpace: 'nowrap' }}>
                  {caption}
                </div>
              )}
            </div>
          )}
          {badges.length > 0 && (
            <div style={{ position: 'absolute', right: '7%', bottom: '11%', display: 'flex' }}>
              {badges.slice(0, 3).map((b, i) => (
                <span
                  key={b}
                  style={{
                    width: 22,
                    height: 22,
                    marginLeft: i ? -6 : 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    background: '#f4f3ef',
                    boxShadow: '0 0 0 2px #2f2f2e',
                    color: '#2b2a28',
                    fontSize: 7,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}>
                  {b}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* its soft drop */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: '6%',
            right: '6%',
            bottom: -8,
            height: 18,
            borderRadius: '50%',
            background: 'radial-gradient(closest-side, rgba(0,0,0,0.5), transparent)',
            filter: 'blur(6px)',
            zIndex: 0,
          }}
        />
      </div>
    </div>
  );
};

export default DocFolder;
